import React, { useState, useEffect, useRef } from "react";
import { Container, Row, Col, Form, Button, Alert } from "react-bootstrap";
import Particle from "../Particle";
import {
  AiFillGithub,
  AiFillInstagram,
  AiOutlineMail,
  AiOutlinePhone,
  AiOutlineEnvironment,
  AiOutlineLinkedin,
} from "react-icons/ai";
import { FaLinkedinIn, FaPaperPlane } from "react-icons/fa";

// Animation component
const AnimatedSection = ({ children, delay = 0, style = {} }) => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  return (
    <div 
      ref={sectionRef}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
        transition: `opacity 0.6s ease-out ${delay}s, transform 0.6s ease-out ${delay}s`,
        ...style
      }}
    >
      {children}
    </div>
  );
};

function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertVariant, setAlertVariant] = useState('success');
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.name || !formData.email || !formData.message) {
      setAlertMessage('Please fill in all required fields.');
      setAlertVariant('danger');
      setShowAlert(true);
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setAlertMessage('Please enter a valid email address.');
      setAlertVariant('danger');
      setShowAlert(true);
      return;
    }

    setIsLoading(true);

    try {
      // Call backend API
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          subject: formData.subject || 'Contact from Portfolio',
          message: formData.message
        })
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setAlertMessage(result.message || 'Thank you! Your message has been sent successfully. I\'ll get back to you soon!');
        setAlertVariant('success');
        setShowAlert(true);
        
        // Reset form on success
        setFormData({
          name: '',
          email: '',
          subject: '',
          message: ''
        });
      } else {
        // Handle API error response
        const errorMessage = result.message || 'Failed to send message. Please try again later.';
        setAlertMessage(errorMessage);
        setAlertVariant('danger');
        setShowAlert(true);
      }

    } catch (error) {
      console.error('Error sending message:', error);
      
      // Network or other error
      setAlertMessage('Unable to send message. Please check your internet connection and try again.');
      setAlertVariant('danger');
      setShowAlert(true);
    } finally {
      setIsLoading(false);
      
      // Hide alert after 5 seconds
      setTimeout(() => {
        setShowAlert(false);
      }, 5000);
    }
  };

  return (
    <Container fluid className="contact-section">
      <Particle />
      <Container>
        <Row style={{ justifyContent: "center", padding: "10px" }}>
          <Col
            md={7}
            style={{
              paddingTop: "30px",
              paddingBottom: "50px",
            }}
          >
            <AnimatedSection delay={0.1}>
              <h1 style={{ fontSize: "2.1em", paddingBottom: "20px", color: "white" }}>
                Get In <strong className="purple">Touch</strong>
              </h1>
              <p style={{ textAlign: "justify", color: "white", fontSize: "1.1em", lineHeight: "1.6" }}>
                I'm always excited to connect with fellow developers, potential collaborators, 
                or anyone interested in discussing technology and innovation. Whether you have 
                a project in mind, want to explore opportunities, or just want to say hello, 
                I'd love to hear from you!
              </p>
              <p style={{ textAlign: "justify", color: "white", fontSize: "1.1em", lineHeight: "1.6" }}>
                Feel free to reach out through the form below or connect with me on social media. 
                I typically respond within 24-48 hours.
              </p>
            </AnimatedSection>
          </Col>
        </Row>

        <Row style={{ justifyContent: "center", paddingBottom: "50px" }}>
          <Col md={6}>
            <AnimatedSection delay={0.2}>
              <div className="contact-form-container">
                <h3 className="contact-form-title">
                  <FaPaperPlane style={{ marginRight: "10px" }} />
                  Send me a message
                </h3>
                
                {showAlert && (
                  <Alert variant={alertVariant} onClose={() => setShowAlert(false)} dismissible>
                    {alertMessage}
                  </Alert>
                )}

                <Form onSubmit={handleSubmit} className="contact-form">
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Name *</Form.Label>
                        <Form.Control
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          className="contact-input"
                          placeholder="Enter your name"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Email *</Form.Label>
                        <Form.Control
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className="contact-input"
                          placeholder="Enter your email"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  
                  <Form.Group className="mb-3">
                    <Form.Label>Subject</Form.Label>
                    <Form.Control
                      type="text"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      className="contact-input"
                      placeholder="What's this about?"
                    />
                  </Form.Group>
                  
                  <Form.Group className="mb-3">
                    <Form.Label>Message *</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={5}
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      className="contact-input contact-textarea"
                      placeholder="Tell me about your project or just say hi!"
                    />
                  </Form.Group>
                  
                  <div style={{ textAlign: "center" }}>
                    <Button 
                      variant="primary" 
                      type="submit" 
                      className="contact-submit-btn"
                      size="lg"
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <>
                          <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" style={{ marginRight: "8px" }}></span>
                          Sending...
                        </>
                      ) : (
                        <>
                          <FaPaperPlane style={{ marginRight: "8px" }} />
                          Send Message
                        </>
                      )}
                    </Button>
                  </div>
                </Form>
              </div>
            </AnimatedSection>
          </Col>

          <Col md={4} style={{ paddingLeft: "30px" }}>
            <AnimatedSection delay={0.3}>
              <div className="contact-info">
                <h3 className="contact-info-title">Let's Connect</h3>
                
                <div className="contact-item">
                  <AiOutlineMail className="contact-icon" />
                  <div>
                    <h5>Email</h5>
                    <a href="mailto:kmrsaharsh@gmail.com" className="contact-link">
                      kmrsaharsh@gmail.com
                    </a>
                  </div>
                </div>

                <div className="contact-item">
                  <AiOutlineEnvironment className="contact-icon" />
                  <div>
                    <h5>Location</h5>
                    <p>Ranchi, Jharkhand, India- 835222</p>
                  </div>
                </div>

                <div className="contact-social">
                  <h5>Follow me on</h5>
                  <ul className="contact-social-links">
                    <li className="contact-social-icons">
                      <a
                        href="https://github.com/saharshkmr/"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="GitHub"
                      >
                        <AiFillGithub />
                      </a>
                    </li>
                    <li className="contact-social-icons">
                      <a
                        href="https://www.linkedin.com/in/saharsh-kumar/"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="LinkedIn"
                      >
                        <FaLinkedinIn />
                      </a>
                    </li>
                    <li className="contact-social-icons">
                      <a
                        href="https://www.instagram.com/saharshrajputt/"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Instagram"
                      >
                        <AiFillInstagram />
                      </a>
                    </li>
                  </ul>
                </div>

                <div className="contact-availability">
                  <div className="availability-status">
                    <div className="status-indicator available"></div>
                    <span>Available for opportunities</span>
                  </div>
                  <p className="availability-text">
                    Open to new projects and collaborations
                  </p>
                </div>
              </div>
            </AnimatedSection>
          </Col>
        </Row>
      </Container>
    </Container>
  );
}

export default Contact;